import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import { DEFAULT_VISIBILITY_WEEKS } from '../../../../utils/constants';

// Components
import Button from '../../../../components/CustomButton/CustomButton';
import Select from '../../../../elements/Select';
import { ScrollSelectWidget } from '../../../../components/GenericForm/Widgets/ScrollSelectWidget';
import DatePicker from '../../../../components/DatePicker/DatePicker';
import WorkPatternPreview from './WorkPatternPreview';
import GeneratedCyclesPreviewModal from './GeneratedCyclesPreviewModal';
import { Textarea } from '../../../../elements/Input';

//Assets
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';

const EditWorkPatternAssignmentsModal = ({
    show,
    onHide,
    t,
    dateFormat,
    state,
    workPatternLoadFn,
    companyShiftMap,
    setState,
    onWorkPatternChange,
    onSave,
    onExecuteSave,
    visibilityWeeks,
}) => {
    const [showAllCycles, setShowAllCycles] = useState(false);

    useEffect(() => {
        if (!show) setShowAllCycles(false);
    }, [show]);

    if (!show || !state) return null;

    const { currentEditEmployeeData, selectedWorkPattern, editStartDate, overrideComment, showConflictView, assignmentConflicts, workPatternDetails, isLoadingDetails, isConflictLoading, patternStartWeek } = state;

    const totalWeeks = workPatternDetails && workPatternDetails.total_weeks ? workPatternDetails.total_weeks : 0;
    const weekOptions = Array.from({ length: totalWeeks }, (_, i) => ({ value: i + 1, label: `${t('Week')} ${i + 1}` }));
    const isShortNotice = editStartDate && moment(editStartDate).isBefore(moment().add((visibilityWeeks || DEFAULT_VISIBILITY_WEEKS) * 7, 'days').startOf('day'));

    return (
        <>
            <Modal centered size="lg" show={show} onHide={onHide}>
                <Modal.Header>
                    <Modal.Title className="fs14 fw-700">
                        {t('Edit')} {t('Work Pattern')} {t('Assignment')} - {currentEditEmployeeData?.employee_name}
                    </Modal.Title>
                    <img src={CloseModalIcon} className="cursor-pointer" alt='close icon' onClick={onHide} />
                </Modal.Header>

                <Modal.Body>
                    {currentEditEmployeeData && (
                        <Row className="mb-4 pb-3 border-bottom">
                            <Col md={12}>
                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <p className="fw-700 fs13 mb-0 text-truncate">{currentEditEmployeeData.template_name}</p>
                                    {currentEditEmployeeData.pattern_start_week > 1 && (
                                        <span className="text-dark fs12 fw-600">
                                            {t('Work Pattern Start Week')}: {currentEditEmployeeData.pattern_start_week}
                                        </span>
                                    )}
                                </div>

                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <p className="fs12 fw-600 text-uppercase mb-0 text-primary-color">
                                        {t('Generated Cycles')}
                                        {currentEditEmployeeData.generated_cycles?.length > 0 && (
                                            <span className="ms-2 fw-500">({currentEditEmployeeData.generated_cycles.length})</span>
                                        )}
                                    </p>
                                    {currentEditEmployeeData.generated_cycles?.length > 5 && (
                                        <span
                                            className="fw-500 fs12 cursor-pointer text-decoration-underline text-primary-color"
                                            onClick={() => setShowAllCycles(true)}
                                        >
                                            {t('View all')}
                                        </span>
                                    )}
                                </div>

                                {currentEditEmployeeData.generated_cycles?.length > 0 ? (
                                    <div className="d-flex flex-wrap gap-3">
                                        {[...currentEditEmployeeData.generated_cycles].reverse().slice(0, 5).map((c, i) => (
                                            <span key={i} className="fs12">
                                                {moment(c.start).format(dateFormat)} &rarr; {moment(c.end).format(dateFormat)}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted fst-italic fs12 mb-0">{t('No cycles generated')}</p>
                                )}
                            </Col>
                        </Row>
                    )}

                    <Row className="mb-3">
                        <Col md={5} xs={12} className="mb-2">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="fw-600 fs13 mb-0">{t('Select')} {t('Work Pattern')}</label>
                                {selectedWorkPattern && (
                                    <Button sm nb onClick={() => setState({ showHistoryModal: true })}>
                                        <i className="fa fa-history me-1 fs12" />
                                        <span className="fs12">{t('View Versions')}</span>
                                    </Button>
                                )}
                            </div>
                            <ScrollSelectWidget
                                loadFn={workPatternLoadFn}
                                prepareOption={row => ({ value: row.id, label: row.name, automation: row.automation, department: row.department })}
                                value={selectedWorkPattern?.value}
                                onChange={(val, option) => onWorkPatternChange(option)}
                                placeholder={t('Select work pattern')}
                                maxMenuHeight={200}
                                menuPortalTarget={document.body}
                                classNamePrefix="edit-wp-select"
                            />
                        </Col>

                        <Col md={4} xs={12} className="mb-2">
                            <label className="fw-600 fs13 mb-2 d-block">{t('Select')} {t('Start date')}</label>
                            <DatePicker
                                isClearable
                                selected={editStartDate}
                                onChange={(date) => setState({ editStartDate: date, showConflictView: false, assignmentConflicts: [], overrideComment: '' })}
                                dateFormat={dateFormat}
                                placeholderText={t('Select Date')}
                                popperProps={{ strategy: 'fixed' }}
                                popperPlacement="bottom-start"
                                minDate={
                                    currentEditEmployeeData?.start_date
                                        ? new Date(Math.max(new Date(currentEditEmployeeData.start_date).getTime(), new Date().setDate(new Date().getDate() + 1)))
                                        : new Date(new Date().setDate(new Date().getDate() + 1))
                                }
                                maxDate={currentEditEmployeeData?.end_date ? new Date(currentEditEmployeeData.end_date) : undefined}
                            />
                        </Col>

                        <Col md={3} xs={12} className="mb-2">
                            <label className="fw-600 fs13 mb-2 d-block">
                                {t('Pattern Start Week')}
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id="pattern-start-week-tooltip">{t('Choose the starting week for the employee. The work pattern will repeat continuously from the selected week.')}</Tooltip>}
                                >
                                    <i className="fa fa-info-circle text-muted ms-1 cursor-pointer" />
                                </OverlayTrigger>
                            </label>
                            <Select
                                options={weekOptions}
                                value={weekOptions.find(o => o.value === patternStartWeek) || weekOptions[0]}
                                onChange={o => setState({ patternStartWeek: o ? o.value : 1 })}
                                menuPortalTarget={document.body}
                                classNamePrefix="edit-wp-select"
                            />
                        </Col>
                    </Row>

                    {isShortNotice && (
                        <div className="mb-3 animate-fade-in">
                            <Textarea
                                label={<span className="fw-600 fs13">{t('Reason for Short Notice Change')} <span className="text-danger">*</span></span>}
                                rows={3}
                                className="h-auto"
                                value={overrideComment}
                                onChange={(e) => setState({ overrideComment: e.target.value })}
                            />
                        </div>
                    )}

                    {showConflictView && (
                        <div>
                            <div className="fs13 text-danger text-center">
                                <strong>{t('Conflict detected')}!</strong>{' '}
                                {t('Some existing cycles overlap with your selected start date. Do you want to replace them with the new work pattern?')}
                            </div>
                            <div className="table-scroll">
                                <Table className="table-sm fs12 table-container">
                                    <thead>
                                        <tr>
                                            <th>{t('Template')} {t('Name')}</th>
                                            <th>{t('Start')} {t('Date')}</th>
                                            <th>{t('End Date')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {assignmentConflicts.map((c, index) => (
                                            <tr key={index}>
                                                <td>{c.template_name}</td>
                                                <td>{moment(c.cycle_start_date).format(dateFormat)}</td>
                                                <td>{c.cycle_end_date ? moment(c.cycle_end_date).format(dateFormat) : t('Ongoing')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {(workPatternDetails || isLoadingDetails) && (
                        <>
                            {selectedWorkPattern && (
                                <small className="text-muted d-block mt-1 text-truncate">
                                    <i className="fa fa-info-circle me-1" />
                                    {t('Template Cycles will be generated based on the version active between its start and end dates.')}
                                </small>
                            )}
                            <WorkPatternPreview
                                t={t}
                                workPatternDetails={workPatternDetails}
                                isLoading={isLoadingDetails}
                                companyShiftMap={companyShiftMap}
                            />
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    {!showConflictView ? (
                        <>
                            <Button onClick={onSave} fill disabled={isConflictLoading}>
                                {isConflictLoading ? (
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                ) : (
                                    t('Save')
                                )}
                            </Button>
                            <Button onClick={onHide}>{t('Cancel')}</Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={() => onExecuteSave(true)} fill danger>
                                {t('Yes')}, {t('Replace All')}
                            </Button>
                            <Button onClick={onHide}>{t('Cancel')}</Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            <GeneratedCyclesPreviewModal
                show={showAllCycles}
                onHide={() => setShowAllCycles(false)}
                cycles={currentEditEmployeeData?.generated_cycles}
                t={t}
                dateFormat={dateFormat}
            />
        </>
    );
};

export default EditWorkPatternAssignmentsModal;